import { DatePipe } from '@angular/common';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

export class CustomValidators {
  static datePipe = new DatePipe('en-US');
  static atLeastOneFieldRequiredValidator(
    control: AbstractControl
  ): { [key: string]: boolean } | null {
    const vatNumber = control.get('vatNumber')?.value;
    const crNumber = control.get('crNumber')?.value;
    const clientType = control.get('clientType')?.value;

    if (
      !(
        vatNumber ||
        crNumber ||
        ['CUSTOMER', 'INTERNATIONAL', 'GOVERNMENT'].includes(clientType?.value)
      )
    ) {
      return { atLeastOneFieldRequired: true };
    }

    return null;
  }

  static maximumThreeMonthsRangeValidator(
    fromDateControlName: string,
    toDateControlName: string
  ) {
    return (group: FormGroup): { [key: string]: boolean } | null => {
      const fromDateControl = group.get(fromDateControlName);
      const toDateControl = group.get(toDateControlName);

      if (fromDateControl && toDateControl) {
        const fromDate = new Date(fromDateControl.value);
        const toDate = new Date(toDateControl.value);

        // Calculate three months later
        const threeMonthsLater = new Date(fromDate);
        threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);

        // Check if toDate is after fromDate and not more than three months later
        if (fromDate > toDate || toDate > threeMonthsLater) {
          return { invalidDateRange: true };
        }
      }

      return null;
    };
  }
  static normalDateRangeValidator(
    fromDateControlName: string,
    toDateControlName: string
  ) {
    return (group: FormGroup): { [key: string]: boolean } | null => {
      const fromDateControl = group.get(fromDateControlName);
      const toDateControl = group.get(toDateControlName);

      if (fromDateControl && toDateControl) {
        const fromDate = new Date(fromDateControl.value);
        const toDate = new Date(toDateControl.value);

        if (fromDate > toDate) {
          return { invalidDateRange: true };
        }
      }

      return null;
    };
  }
  static arrivalDateValidator(
    toDate: string | null,
    fromDate: string | null
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const departureDateTimeControl = control.parent?.get('departureDateTime');
      const arrivalDateTime = control.value;
      const departureDateTime = departureDateTimeControl?.value;

      if (!arrivalDateTime || !departureDateTime) {
        return null;
      }

      const arrivalDateTimeObj = new Date(arrivalDateTime);
      const departureDateTimeObj = new Date(departureDateTime);

      // Check if arrivalDateTime is greater than departureDateTime
      if (arrivalDateTimeObj <= departureDateTimeObj) {
        return { arrivalBeforeDeparture: true };
      }

      // If fromDate and toDate are provided, check if arrivalDateTime is within the range
      if (fromDate && toDate) {
        const fromDateObj = new Date(fromDate);
        const toDateObj = new Date(toDate);
        fromDateObj.setHours(0, 0, 0, 0);
        toDateObj.setHours(23, 59, 59, 999);

        if (
          arrivalDateTimeObj < fromDateObj ||
          arrivalDateTimeObj > toDateObj
        ) {
          return { invalidArrivalDateRange: true };
        }
      }

      return null;
    };
  }

  static departureDateValidator(
    toDate: string | null,
    fromDate: string | null
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const departureDateTime = control.value;

      const arrivalDateTimeControl = control.parent?.get('arrivalDateTime');
      const arrivalDateTime = arrivalDateTimeControl?.value;

      const departureDateTimeObj = new Date(departureDateTime);

      const arrivalDateTimeObj = new Date(arrivalDateTime);
      // If fromDate and toDate are provided, check if departureDateTime is within the range
      if (fromDate && toDate) {
        const fromDateObj = new Date(fromDate);
        const toDateObj = new Date(toDate);

        fromDateObj.setHours(0, 0, 0, 0);
        toDateObj.setHours(23, 59, 59, 999);
        if (
          departureDateTimeObj < fromDateObj ||
          departureDateTimeObj > toDateObj
        ) {
          return { invalidDepartureDateRange: true };
        }
      }

      return null;
    };
  }
  static fromToDateValidator(
    fromDateControlName: string,
    toDateControlName: string
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const fromDateControl = group.get(fromDateControlName);
      const toDateControl = group.get(toDateControlName);

      if (!fromDateControl || !toDateControl) {
        return null;
      }

      const fromDate = fromDateControl.value;
      const toDate = toDateControl.value;

      if (fromDate && !toDate) {
        return { toDateRequired: true };
      }

      return null;
    };
  }

  static creditedValuesWithinOriginal(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const quantity = group.get('quantity')?.value;
      const creditedQuantity = group.get('creditedQuantity')?.value;
      const unitPrice = group.get('unitPrice')?.value;
      const creditedUnitPrice = group.get('creditedUnitPrice')?.value;

      if (creditedQuantity && quantity && +creditedQuantity > +quantity) {
        return { creditedQuantityExceeds: true };
      }

      if (creditedUnitPrice && unitPrice && +creditedUnitPrice > +unitPrice) {
        return { creditedUnitPriceExceeds: true };
      }

      return null;
    };
  }
}
